/**
 * tracking-form-context.js
 */

import React from 'react';
import axios from 'axios';
import { Box, Typography } from '@mui/material';
import * as Sentry from "@sentry/react";
import { Types } from 'utils/constants';
import { randomInt } from 'utils/random';
import usePrevious from 'utils/use-previous';
import { getCookie, setCookie } from 'utils/cookie';
import { renderForMobileContext } from 'utils/app-context';
import { UserContext } from 'contexts/user/user-context';
import DateDistance from 'components/date-distance/date-distance';
import { TRACK_TYPE, TRACKING_STATE, TRACK_COMPLETION_ACTION, TRACKING_FORM_STORAGE_KEY } from '../tracking-common';
import { TrackContext } from '../track-context';
import { TrackingDrawerContext } from '../input-drawers/drawer-context';
import { LiveTrackingContext } from './live-tracking-context';
import { DogsContext } from 'contexts/dogs/dogs-context';

const UPDATE_LEAD_TRACKS_TIME = 20000; // 20 seconds
const UPDATE_EXERCISES_TIME = 30000; // 30 seconds
const TRACK_VISIBILITY_COOKIE_NAME = 'trackVisibility';
export const TRACK_VISIBILITY = {
  HIDDEN: 'hidden',
  START_LOCATION: 'start-location',
  VISIBLE: 'visible'
};
const DEFAULT_FOLLOW_EXERCISE_OPTION = {
  id: null,
  name: '<Create new exercise automatically>'
};
const DEFAULT_FOLLOW_LEAD_TRACK_OPTION = {
  id: null,
  name: '<Follow without laid track>'
};


const generateTrackName = (lastName) => {
  return `${lastName ? `${lastName} ` : ''}Track ${randomInt(100, 999)}`;
};


export const TrackingFormContext = React.createContext();


export const TrackingFormProvider = (props) => {
  const {
    children,
  } = props;
  const { user } = React.useContext(UserContext);
  const { setTracks } = React.useContext(TrackContext);
  const { 
    isTrackingOpen, 
    currentLocation, 
    activeTrack,
    isActive, 
    trackingState, 
    trackCompletionAction, 
    resetTracking,
  } = React.useContext(LiveTrackingContext);
  const { setIsDrawerLoading } = React.useContext(TrackingDrawerContext);
  const { getActiveDogs } = React.useContext(DogsContext);
  const currentLocationRef = React.useRef();
  const updateExerciseTimeout = React.useRef();
  const updateLeadTracksTimeout = React.useRef();
  const prevTrackingState = usePrevious(trackingState);
  const prevUser = usePrevious(user);
  const userLastName = user?.lastName ?? '';
  const activeDogs = React.useMemo(() => getActiveDogs(), [getActiveDogs]);
  const availableTrackingDogs = React.useMemo(() => {
    return activeDogs?.filter(dog =>
      dog.patrolAttributes.find(pt => pt.id === Types.Patrol.TRACKING)) ?? [];
  }, [activeDogs]);


  const [state, setState] = React.useState(() => {
    // get trackType from active track if exists
    const trackVisibilityCookieString = getCookie(TRACK_VISIBILITY_COOKIE_NAME);
    return {
      trackType: activeTrack?.type ?? (user && user.role.isHandler ? TRACK_TYPE.DEPLOYMENT : TRACK_TYPE.LEAD),
      availableDogs: null, 
      dog: null,
      trackName: generateTrackName(user),
      availableExercises: null,
      followExercise: null,
      availableLeadTracks: null,
      followLeadTrack: null,
      trackVisibility: trackVisibilityCookieString ? JSON.parse(trackVisibilityCookieString) : {}
    };
  });

  const isFollowTabReady = Boolean(state.followExercise) && Boolean(state.followLeadTrack);
  const selectedTrackVisibility = state.followLeadTrack?.id
    ? state.trackVisibility[state.followLeadTrack.id]
    : TRACK_VISIBILITY.HIDDEN;

    

  // -----------------------------------------------------------------------------------------
  // private methods / effects
  // -----------------------------------------------------------------------------------------


  // update dog availability when:
  //    - track type changes to deployment or follow
  //    - follow tracking exercise selection changes
  //    - exercise list is updated
  const updateAvailableDogs = React.useCallback(() => {
    setState(p => {
      let availableDogs = [];
      const exerciseId = p.followExercise?.id;

      // if deployment or follow + create exer -> use all available tracking dogs
      if (p.trackType === TRACK_TYPE.DEPLOYMENT || (p.trackType === TRACK_TYPE.FOLLOW && !exerciseId)) {
        availableDogs = availableTrackingDogs;
      } 
      // if follow + specific exer -> disable dogs that have already run a track for this exer
      else if (p.trackType === TRACK_TYPE.FOLLOW && exerciseId) {
        const availableDogsForExercise = p.availableExercises
          ?.find(exer => exer.id === exerciseId)
          ?.dogOptions
          ?.filter(dog => dog.completionTrackingId === null)
          ?.map(dog => dog.id);

        if (!availableDogsForExercise) {
          availableDogs = availableTrackingDogs;
        } else {
          availableDogs = availableTrackingDogs.map(dog => ({
            ...dog,
            isDisabled: !availableDogsForExercise.includes(dog.id)
          }));
        }
      }

      return {
        ...p,
        dog: availableDogs.find(dog => !dog.isDisabled),
        availableDogs,
      }
    });
  }, [availableTrackingDogs]);


  // update available exercises list. 
  // DO NOT CALL DIRECTLY since it contains a recursive timeout.
  const updateExercises = React.useCallback(async () => {
    if (user) {
      try {
        const response = await axios.get(`/training/exercise/tracks`);

        if (response.status === 200) {
          const availableExercises = [
            ...response.data,
            DEFAULT_FOLLOW_EXERCISE_OPTION
          ];

          setState(p => {
            // determine selected exercise (object equality means options have changed)
            let followExercise = p.followExercise
              ? availableExercises.find(e => e.id === p.followExercise?.id)
              : null;
            if (!followExercise) {
              followExercise = availableExercises[0];
            }

            return {
              ...p,
              availableExercises,
              followExercise
            };
          });     
          updateAvailableDogs();
        } 
      } catch {}

      updateExerciseTimeout.current = setTimeout(() => {
        updateExercises();
      }, UPDATE_EXERCISES_TIME);
    }
  }, [user, updateAvailableDogs]);
  

  // gets and formats available lead tracks. 
  // DO NOT CALL DIRECTLY since it contains a recursive timeout.
  const updateLeadTracks = React.useCallback(async (latitude, longitude) => {
    if (user && latitude && longitude) {
      try {
        const queryParams = `?latitude=${latitude}&longitude=${longitude}`;
        const response = await axios.get(`/exercise-lead-track/recent-local${queryParams}`, { baseURL: process.env.REACT_APP_TRACKING_HOST });
        if (response.status === 200) {
          const processedTracks = response.data.map(track => ({
            ...track,
            type: TRACK_TYPE.LEAD,
            label: (
              <Box className='flex-hc'>
                <Typography sx={{ mr: 1 }}>{track.name}</Typography>
                <Typography variant='caption' sx={{ color: 'sys.onSurfaceVariant' }}>
                  (<DateDistance dateStart={track.dateTimeCreated} suffix='ago' concise />)
                </Typography>
              </Box>
            )
          }));

          const availableLeadTracks = [
            ...processedTracks,
            DEFAULT_FOLLOW_LEAD_TRACK_OPTION
          ];

          setState(p => {
            // determine selected lead track (object equality means options have changed)
            let followLeadTrack = p.followLeadTrack
              ? availableLeadTracks.find(t => t.id === p.followLeadTrack?.id)
              : null;
            if (!followLeadTrack) {
              followLeadTrack = availableLeadTracks[0];
            }

            return {
              ...p,
              availableLeadTracks,
              followLeadTrack
            };
          });
        }
      } catch {}

      updateLeadTracksTimeout.current = setTimeout(() => {
        updateLeadTracks(currentLocationRef.current.latitude, currentLocationRef.current.longitude);
      }, UPDATE_LEAD_TRACKS_TIME);
    }
  }, [user]);


  // stops lead track update interval
  const clearLeadTrackTimeout = React.useCallback(() => {
    if (updateLeadTracksTimeout.current) {
      clearTimeout(updateLeadTracksTimeout.current);
      updateLeadTracksTimeout.current = null;
    }
  }, []);


  // stops exercise update interval
  const clearExerciseTimeout = React.useCallback(() => {
    if (updateExerciseTimeout.current) {
      clearInterval(updateExerciseTimeout.current);
      updateExerciseTimeout.current = null;
    }
  }, []);


  // show loading when follow tab doesn't have exer/lead selections
  React.useEffect(() => {
    if (state.trackType === TRACK_TYPE.FOLLOW) {
      setIsDrawerLoading(!isFollowTabReady);
    }
  }, [state.trackType, isFollowTabReady, setIsDrawerLoading]);

  
  // after saving/completing a follow track:
  // - clear selected exercise.
  // - clear selected lead track and remove from available list.
  React.useEffect(() => {
    if (state.trackType === TRACK_TYPE.FOLLOW 
      && trackingState === TRACKING_STATE.UNSTARTED 
      && prevTrackingState === TRACKING_STATE.STOPPED
      && trackCompletionAction !== TRACK_COMPLETION_ACTION.DISCARD
    ) {
      setState(p => ({
        ...p,
        availableLeadTracks: p.availableLeadTracks.filter(t => t.id && t.id !== p.followLeadTrack.id),
        followExercise: null,
        followLeadTrack: null
      }));
    }
  }, [state.trackType, trackingState, prevTrackingState, trackCompletionAction]);


  // state may have been initialized before user, so re-init relevant properties
  React.useEffect(() => {
    if (user !== prevUser && trackingState === TRACKING_STATE.UNSTARTED) {
      setState(p => ({
        ...p,
        trackType: user && user.role.isHandler ? TRACK_TYPE.DEPLOYMENT : TRACK_TYPE.LEAD,
      }));
    }
  }, [trackingState, user, prevUser]);


  // initial update of dogs list 
  React.useEffect(() => {
    if (isTrackingOpen) {
      updateAvailableDogs();
    }
  }, [isTrackingOpen, updateAvailableDogs]);


  // auto-generate new track name after finished laying a track
  React.useEffect(() => {
    if (trackingState === TRACKING_STATE.UNSTARTED && state.trackType === TRACK_TYPE.LEAD) {
      setState(p => ({
        ...p,
        trackName: generateTrackName(userLastName)
      }));
    }
  }, [userLastName, trackingState, state.trackType]);


  // update tracks based on laid track visibility setting. 
  // due to misfiring at improper times, only proceed if trackingState is unstarted.
  React.useEffect(() => {
    if (trackingState !== TRACKING_STATE.UNSTARTED) {
      return;
    }

    if (state.trackType !== TRACK_TYPE.FOLLOW) {
      setTracks([]);
      return;
    }

    if (selectedTrackVisibility === TRACK_VISIBILITY.HIDDEN) {
      setTracks([]);
    } else if (selectedTrackVisibility === TRACK_VISIBILITY.START_LOCATION) {
      setTracks([{
        ...state.followLeadTrack,
        points: [state.followLeadTrack.points[0]]
      }]);
    } else if (selectedTrackVisibility === TRACK_VISIBILITY.VISIBLE) {
      setTracks([state.followLeadTrack]);
    }
  }, [
    trackingState,
    state.trackType, 
    state.followLeadTrack, 
    selectedTrackVisibility, 
    setTracks
  ]);


  // control the starting/stopping of lead track and exercise update intervals
  React.useEffect(() => {
    currentLocationRef.current = currentLocation;

    if (currentLocation && isTrackingOpen && trackingState === TRACKING_STATE.UNSTARTED && !updateLeadTracksTimeout.current) {
      updateLeadTracks(currentLocation.latitude, currentLocation.longitude);
    } 

    if (isTrackingOpen && trackingState === TRACKING_STATE.UNSTARTED && state.trackType === TRACK_TYPE.FOLLOW && !updateExerciseTimeout.current) {
      updateExercises();
    } 
    
    if (!isTrackingOpen || trackingState !== TRACKING_STATE.UNSTARTED) {
      clearLeadTrackTimeout();
    }

    if (!isTrackingOpen || trackingState !== TRACKING_STATE.UNSTARTED || state.trackType !== TRACK_TYPE.FOLLOW) {
      clearExerciseTimeout();
    }
  }, [
    currentLocation, 
    isTrackingOpen, 
    trackingState, 
    state.trackType,
    updateExercises,
    clearExerciseTimeout,
    updateLeadTracks,
    clearLeadTrackTimeout,
  ]);


  // save state
  React.useEffect(() => {
    if (isActive) {
      localStorage.setItem(TRACKING_FORM_STORAGE_KEY, JSON.stringify({
        trackType: state.trackType,
        availableExercises: state.availableExercises,
        followExercise: state.followExercise,
        availableLeadTracks: state.availableLeadTracks,
        followLeadTrack: state.followLeadTrack
      }));
    } 
    // NOTE: clearing on reset happens in tracking-context
  }, [
    isActive, 
    state.trackType, 
    state.availableExercises,
    state.followExercise,
    state.availableLeadTracks,
    state.followLeadTrack,
  ]);


  // restore state
  React.useEffect(() => {
    if (renderForMobileContext()) {
      const savedStateRaw = localStorage.getItem(TRACKING_FORM_STORAGE_KEY);
      const savedState = savedStateRaw ? JSON.parse(savedStateRaw) : null;
      if (savedState) {
        // basic sanity check
        if (![TRACK_TYPE.DEPLOYMENT, TRACK_TYPE.LEAD, TRACK_TYPE.FOLLOW].includes(savedState.trackType)) {
          // limit logged points to prevent over-size data
          savedState.availableLeadTracks.forEach(track => {
            if (track.points) {
              track.points = track.points.slice(0, 10)
            }
          });
          if (savedState.followLeadTrack?.points) {
            savedState.followLeadTrack.points = savedState.followLeadTrack.points.slice(0, 10);
          }

          Sentry.captureMessage(
            'Tracking form context restore failed sanity check', 
            { extra: { [TRACKING_FORM_STORAGE_KEY]: JSON.stringify(savedState) }});
          resetTracking();
          return;
        }

        setState(p => ({
          ...p,
          trackType: savedState.trackType,
          availableExercises: savedState.availableExercises,
          followExercise: savedState.followExercise,
          availableLeadTracks: savedState.availableLeadTracks,
          followLeadTrack: savedState.followLeadTrack
        }));
      }
    }
  }, [resetTracking]);


  // cleanup intervals on unmount
  React.useEffect(() => {
    return () => {
      clearExerciseTimeout();
      clearLeadTrackTimeout();
    }
  }, [clearExerciseTimeout, clearLeadTrackTimeout]);



  // -----------------------------------------------------------------------------------------
  // public methods
  // -----------------------------------------------------------------------------------------


  const setTrackType = React.useCallback((trackType) => {
    setState(p => ({
      ...p,
      trackType,
      // clear selected exercise on tab change
      followExercise: null,
      // when selecting follow tab, auto-select first available lead track
      followLeadTrack: trackType === TRACK_TYPE.FOLLOW 
        ? p.availableLeadTracks[0] 
        : p.followLeadTrack
    }));

    updateAvailableDogs();
  }, [updateAvailableDogs]);


  const setDog = React.useCallback((dog) => {
    setState(p => ({
      ...p,
      dog
    }));
  }, []);


  const setTrackName = React.useCallback((trackName) => {
    setState(p => ({
      ...p,
      trackName
    }));
  }, []);


  const setFollowExercise = React.useCallback((followExercise) => {
    setState(p => ({
      ...p,
      followExercise,
    }));
    updateAvailableDogs();
  }, [updateAvailableDogs]);


  const setFollowLeadTrack = React.useCallback((followLeadTrack) => {
    setState(p => ({
      ...p,
      followLeadTrack,
    }));
  }, []);


  const setFollowLeadTrackVisibility = React.useCallback((followLeadTrackVisibility) => {
    if (state.followLeadTrack.id === null) {
      return;
    }

    setState(p => {
      const trackVisibility = {
        ...p.trackVisibility,
        [state.followLeadTrack.id]: followLeadTrackVisibility
      };

      // set cookie to track visibility.
      // TODO: this is a temp solution. when we change to persisting tracking state in app storage this belongs there too.
      setCookie(TRACK_VISIBILITY_COOKIE_NAME, JSON.stringify(trackVisibility), 3);

      return { ...p, trackVisibility };
    });
  }, [state.followLeadTrack?.id]);



  // -----------------------------------------------------------------------------------------
  // final state value
  // -----------------------------------------------------------------------------------------

  const value = React.useMemo(() => ({
    ...state,
    setTrackType,
    setDog,
    setTrackName,
    setFollowExercise,
    setFollowLeadTrack,
    setFollowLeadTrackVisibility
  }), [
    state,
    setTrackType,
    setDog,
    setTrackName,
    setFollowExercise,
    setFollowLeadTrack,
    setFollowLeadTrackVisibility
  ]);

  
  return (<>
    <TrackingFormContext.Provider 
      value={value}
      children={children}
    />
  </>);
}